import { t } from "@/i18n-js/instance";
import { ApiError } from "@circle-react/config/CustomErrors";
import { safeJsonParse } from "./safeJsonParse";

export interface ErrorJsonBody {
  error_details?: string;
  failure?: {
    message: string;
  };
  message?: string;
  view_only_masquerading?: boolean;
}

export interface JsonFetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

export async function jsonFetchRequest<ResponseData>(
  url: string,
  options?: JsonFetchOptions,
) {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...options?.headers,
    },
    credentials: "same-origin",
  });

  if (response.ok) {
    try {
      const text = await response.text();
      if (text.length === 0) return undefined as ResponseData;
      return JSON.parse(text) as ResponseData;
    } catch (error: unknown) {
      const details = error instanceof Error ? error.message : "";
      throw new ApiError({
        message: `Failed to parse response`,
        status: response.status,
        statusText: response.statusText,
        errorDetails: details,
      });
    }
  } else {
    return await throwApiError(response);
  }
}

export const throwApiError = async (response: Response) => {
  let json: ErrorJsonBody;

  try {
    const text = await response.text();
    json = text.length > 0 ? (JSON.parse(text) as ErrorJsonBody) : {};
  } catch {
    json = { message: `Failed to parse error response` };
  }

  const { status, statusText } = response;

  if (json?.view_only_masquerading) {
    throw new ApiError({
      message: t("view_only_error"),
      status,
      statusText,
      errorDetails: t("view_only_error"),
      body: json,
      response,
    });
  }

  const message =
    json?.message || json?.failure?.message || "Failed to retrieve data!";

  const { error_details = json?.message } = json;

  throw new ApiError({
    message,
    status,
    statusText,
    errorDetails: error_details,
    body: json,
    response,
  });
};

export const reactQueryGet = <ResponseData = any>(url: string) =>
  jsonFetchRequest<ResponseData>(url, { method: "GET" });

export const reactQueryPut = <ResponseData, BodyParameters extends object>(
  url: string,
  bodyParams?: BodyParameters,
) =>
  jsonFetchRequest<ResponseData>(url, {
    method: "PUT",
    body: JSON.stringify(bodyParams),
  });

export const reactQueryPatch = <ResponseData, BodyParameters extends object>(
  url: string,
  bodyParams?: BodyParameters,
) =>
  jsonFetchRequest<ResponseData>(url, {
    method: "PATCH",
    body: JSON.stringify(bodyParams),
  });

export const reactQueryPost = <
  ResponseData = any,
  BodyParameters extends object = object,
>(
  url: string,
  bodyParams?: BodyParameters,
) =>
  jsonFetchRequest<ResponseData>(url, {
    method: "POST",
    body: JSON.stringify(bodyParams),
  });

export const reactQueryDelete = <ResponseData, BodyParameters extends object>(
  url: string,
  bodyParams?: BodyParameters,
) => {
  const options: JsonFetchOptions = {
    method: "DELETE",
  };

  if (bodyParams) {
    options.body = JSON.stringify(bodyParams);
  }

  return jsonFetchRequest<ResponseData>(url, options);
};

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export interface FetchOptions extends Partial<RequestInit> {
  forReactQuery?: boolean;
  headers?: Record<string, string>;
}

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export type OptionsType = FetchOptions | undefined;

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export const serializeJsonResponse = async <ResponseData>(
  response: Response,
): Promise<ResponseData | undefined | Record<string, never>> => {
  if (response.ok) {
    const text = await response.text();
    if (text.length > 0) {
      const parsed = safeJsonParse<ResponseData>(text);
      return parsed || {};
    }
    return {};
  }

  return throwApiError(response);
};

/**
 * @deprecated Please use jsonFetchRequest from backendRequestHelpers.ts instead, or write your own fetch() call
 */
export async function fetchRequest<ResponseData>(
  url: string,
  options: OptionsType = {},
): Promise<Response | ResponseData | Record<string, never> | undefined> {
  const {
    headers = {},
    forReactQuery: isForReactQuery,
    ...passThroughOptions
  } = options;

  const response = await fetch(url, {
    ...passThroughOptions,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...headers,
    },
    credentials: "same-origin",
  });

  if (!isForReactQuery) {
    return response;
  }

  return serializeJsonResponse<ResponseData>(response);
}

/**
 * Detects if an error is a network-related error using robust checks
 * instead of fragile string matching on error messages.
 *
 * Network errors typically occur when:
 * - The fetch request fails due to network connectivity issues
 * - DNS resolution fails
 * - Connection is refused or times out
 * - CORS issues prevent the request
 *
 * @param error - The error to check
 * @returns true if the error appears to be network-related
 */
export const isNetworkError = (error: unknown): boolean => {
  // Check for TypeError which is commonly thrown by fetch() for network issues
  if (error instanceof TypeError) {
    // Additional checks to ensure it's actually a network error
    // and not some other TypeError
    const errorMessage = error.message.toLowerCase();

    // Common network error patterns across browsers
    const networkErrorPatterns = [
      "failed to fetch",
      "load failed",
      "network error",
      "networkerror",
      "fetch error",
      "connection refused",
      "connection failed",
      "dns",
      "timeout",
      "cors",
      "net::",
    ];

    return networkErrorPatterns.some(pattern => errorMessage.includes(pattern));
  }

  // Check for DOMException which can also indicate network issues
  if (error instanceof DOMException) {
    // Network-related DOMException names
    const networkDOMExceptionNames = [
      "NetworkError",
      "AbortError",
      "TimeoutError",
    ];

    return networkDOMExceptionNames.includes(error.name);
  }

  // Check for errors with specific network-related names
  if (error instanceof Error) {
    const networkErrorNames = ["NetworkError", "AbortError", "TimeoutError"];

    return networkErrorNames.includes(error.name);
  }

  return false;
};
